import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, untracked } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import {
  DateTimeAdapter,
  optionsProviders,
  OWL_DATE_TIME_FORMATS,
  OWL_DATE_TIME_LOCALE,
  OwlDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { MomentDateTimeAdapter } from '@danielmoncada/angular-datetime-picker-moment-adapter';
import moment from 'moment-timezone';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MeasurementSystem } from '@models/module/fields/enums/measurementSystem';
import { TableFieldValueType } from '@models/module/fields/main';
import { TenantSettingsEnum } from '@models/tenant';
import { TableFieldComponent } from '@module-definition/table-field/table-field.component';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { CipoControlComponent } from '@shared/components/fields/cipo-control/cipo-control.component';
import { DateTimeFormatService } from '@shared/components/fields/cipo-date-time/date-time-format.service';
import { CipoOutlineFieldComponent } from '@shared/components/fields/cipo-outline-field/cipo-outline-field.component';
import { CipoWeatherTableConfig } from '@shared/components/fields/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules';
import { NotificationService, UtilsService, ValidatorResponse } from '@shared/services';
import { UserStore } from '@signal-store/index';
import { TenantStore } from '@signal-store/tenant.store';
import { debounceTime, Subscription } from 'rxjs';
import { WeatherTableService } from './weather-table.service';

@Component({
  selector: 'cipo-weather-table-field',
  imports: [
    CipoFormModule,
    MatTableModule,
    MatSortModule,
    OwlDateTimeModule,
    NgxSkeletonLoaderModule,
    DragDropModule,
    MatIconModule,
    CipoControlComponent,
    CipoOutlineFieldComponent,
    FlexLayoutModule,
    MatMenuModule,
    MatButtonModule,
    TranslateModule,
  ],
  providers: [
    ...optionsProviders,
    { provide: DateTimeAdapter, useClass: MomentDateTimeAdapter, deps: [OWL_DATE_TIME_LOCALE] },
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: (dateTimeFormatService: DateTimeFormatService) => dateTimeFormatService.getFormats(),
      deps: [DateTimeFormatService],
    },
    {
      provide: OWL_DATE_TIME_LOCALE,
      useFactory: (dateTimeFormatService: DateTimeFormatService) => dateTimeFormatService.getLocale(),
      deps: [DateTimeFormatService],
    },
  ],
  styleUrl: './../table-field/table-field.component.scss',
  templateUrl: './../table-field/table-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeatherTableFieldComponent extends TableFieldComponent implements OnInit {
  private notify = inject(NotificationService);
  private weatherService = inject(WeatherTableService);
  private tenantStore = inject(TenantStore);
  private utilsService = inject(UtilsService);
  private userStore = inject(UserStore);

  date = input<string>(null);
  location = input<number>(null);
  editMode = input(false);
  includeHistory = input(false);

  dateAndLocation = computed(() => ({ date: this.date(), location: this.location() }));
  weatherTimeKey = computed(() => this.tableFields()?.find(field => field.name === 'weather_time')?.key);
  weatherTemperatureKey = computed(() => this.tableFields()?.find(field => field.name === 'weather_temperature')?.key);
  weatherWindKey = computed(() => this.tableFields()?.find(field => field.name === 'weather_wind')?.key);
  weatherConditionsKey = computed(() => this.tableFields()?.find(field => field.name === 'weather_conditions')?.key);
  weatherPrecipitationKey = computed(
    () => this.tableFields()?.find(field => field.name === 'weather_precipitation')?.key,
  );

  timeFieldSubscription: Subscription;
  tableFormArraySubscription: Subscription;
  lastRowInEditMode: number = null;
  userMeasurementSystem: keyof typeof MeasurementSystem;

  constructor() {
    super();

    effect(() => {
      const tableFields = this.tableFields();
      if (!tableFields?.length) {
        return;
      }
      this.tableFields().forEach(field => {
        const measurementType =
          field.name === 'weather_temperature'
            ? 'temperature'
            : field.name === 'weather_wind'
              ? 'speed'
              : field.name === 'weather_precipitation'
                ? 'precipitation'
                : null;
        if (measurementType) {
          field.suffix = this.utilsService.getMeasurementUnit(measurementType, this.userMeasurementSystem);
        }
        if (field.name === 'weather_time') {
          // prevent clearing the time field input when the weather data is loading or when the time value is changed programmatically, because the time field value is required to get the weather data for the correct time
          field.preventClearInput = true;
        }
      });
    });

    effect(() => {
      this.addTimeFieldValidation();
    });

    effect(() => {
      const formArray = this.tableFormArray();
      if (!formArray) return;
      this.tableFormArraySubscription?.unsubscribe();

      /* listen for table value changes and convert back the values to metric measurement system */
      this.tableFormArraySubscription = formArray.valueChanges
        .pipe(debounceTime(DEBOUNCE_TIMES.default))
        .subscribe(formArrayValue => {
          const oldValue = this.control().value;
          const metricMeasurementValues = (formArrayValue ?? []).map((row, index) => {
            if (this.lastRowInEditMode !== null && index === this.lastRowInEditMode) {
              row = this.convertTableDataToMetricMeasurementSystem(row as TableFieldValueType);
            }
            if (oldValue && oldValue[index] && oldValue[index]['empty']) {
              // preserve the 'empty' property to keep showing the skeleton loader if the weather data is still loading
              row['empty'] = oldValue[index]['empty'];
            }
            return row;
          });

          this.control().setValue(metricMeasurementValues as any);
          this.control().markAsDirty();
          this.control().updateValueAndValidity();
        });
    });

    effect(() => {
      const { date, location } = this.dateAndLocation();
      untracked(() => {
        const hasDateAndLocationSet = !!date && location > 0;
        this.loadWeatherDataOnDateOrLocationChanged(hasDateAndLocationSet);
      });
    });
  }

  addTimeFieldValidation() {
    if (!this.weatherTimeKey()) {
      return;
    }
    // add time field required validator to the table form control because the time field is required to get the weather data for the correct time
    // time field is prevented from being cleared, this is more a safety measure
    this.validators.set('requiredTimeField', this.requiredTimeField(this.weatherTimeKey()));
    this.updateValidators(this.control());
  }

  removeTimeFieldValidation() {
    if (!this.weatherTimeKey()) {
      return;
    }
    // remove time field required validator from the table form control
    this.validators.delete('requiredTimeField');
    this.updateValidators(this.control());
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.userMeasurementSystem = this.tenantStore.getSetting(TenantSettingsEnum.measurement_system)
      .calculatedValue as keyof typeof MeasurementSystem;

    this.loadExistingWeatherData();
  }

  insertRow() {
    if (!this.date() || !this.location()) {
      // both date and location are required to add a new row because we need the date and the location to get the weather data for the correct place
      this.notify.error('weatherTable.disabledTitle');
      return;
    }

    this.removeTimeFieldValidation();
    this.convertLastEditedRowToMetricMeasurementSystem();
    super.insertRow();

    // set inserted row in edit mode
    this.lastRowInEditMode = this.activeRowIndex();

    // After inserting the row, we want to load the weather data for that row.
    // We need to do this after the row has been inserted because we need the form controls to be created before we can set their values
    this.loadWeatherDataForCurrentRow();
  }

  editRow(index: number) {
    if (this.lastRowInEditMode === index) {
      return;
    }

    this.cancelWatchForTimeChangesForCurrentRow();
    this.convertLastEditedRowToMetricMeasurementSystem();
    this.convertCurrentRowToUserMeasurementSystem(index);

    // preserve the last row in edit mode
    this.lastRowInEditMode = index;

    // prepare row for edit
    super.editRow(index);

    // listen for time changes so we can update the weather data when the time changes
    this.watchForTimeChangesForCurrentRow();
  }

  drop(event: CdkDragDrop<string>) {
    this.cancelWatchForTimeChangesForCurrentRow();
    this.convertLastEditedRowToMetricMeasurementSystem();

    super.drop(event);

    if (event.previousIndex === this.lastRowInEditMode) {
      this.lastRowInEditMode = event.currentIndex;
    }

    this.convertCurrentRowToUserMeasurementSystem(this.lastRowInEditMode);
    this.watchForTimeChangesForCurrentRow();
  }

  deleteRowComplete(index: number) {
    this.cancelWatchForTimeChangesForCurrentRow();

    super.deleteRowComplete(index);

    this.lastRowInEditMode = this.activeRowIndex();
    this.watchForTimeChangesForCurrentRow();
  }

  private loadExistingWeatherData() {
    this.loading.set(true);
    this.externalDataLoading.set(true);
    const field = this.fieldData() as CipoWeatherTableConfig;
    this.weatherService
      .getInstancesData(field.id, field.entityInstanceId, field.actionInstanceId, null, this.location())
      .subscribe({
        next: weather => {
          // only set the weather data if there is data available, otherwise the control will be marked as changed
          if (weather.data && weather.data.length) {
            // fill the table with the weather data but do not emit event because we don't want to mark the control as dirty when loading the existing data
            this.control().setValue(this.convertWeatherDataRows(weather.data), { emitEvent: false });
            this.tableFormArray.set(this.createFormArray());
            this.watchForTimeChangesForCurrentRow();
          }
        },
        complete: () => {
          this.loading.set(false);
          setTimeout(() => {
            this.externalDataLoading.set(false);
          }, DEBOUNCE_TIMES.default);
        },
      });
  }

  private convertLastEditedRowToMetricMeasurementSystem() {
    // convert existing table values to metric measurement system before, because the view components expect metric measurement system values
    this.tableFormArray().setValue(
      this.tableFormArray()
        .getRawValue()
        .map((row: TableFieldValueType, index: number) => {
          if (this.lastRowInEditMode !== null && index === this.lastRowInEditMode) {
            return this.convertTableDataToMetricMeasurementSystem(row);
          }
          return this.addNullValuesOnMissingFields(row);
        }),
    );
  }

  private convertCurrentRowToUserMeasurementSystem(index: number) {
    // convert existing table values to user measurement system, because the view components expect user measurement system values
    this.tableFormArray().setValue(
      this.tableFormArray()
        .getRawValue()
        .map((row: TableFieldValueType, rowIndex: number) => {
          if (rowIndex === index) {
            return this.convertTableDataToUserMeasurementSystem(row);
          }
          return this.addNullValuesOnMissingFields(row);
        }),
    );
  }

  private loadWeatherDataOnDateOrLocationChanged(hasDateAndLocationSet: boolean) {
    const rows = this.tableFormArray()?.value ?? [];
    if (!Array.isArray(rows) || !rows.length) {
      return;
    }

    if (!hasDateAndLocationSet && this.tableFormArray().value?.length) {
      // if there is no date or location, we can't get the weather data
      // so we clear the table to prevent showing wrong weather data for the previously selected date and location
      this.tableFormArray().clear();
      this.table().renderRows();
      this.activeRowIndex.set(null);
      return;
    }

    const dates = rows.map(row => this.getDateWithCustomTime(row[this.weatherTimeKey()] as string));
    this.markRowsAsEmpty(true);
    this.externalDataLoading.set(true);
    this.cancelWatchForTimeChangesForCurrentRow();
    const field = this.fieldData();

    this.weatherService.getDataByTimesAndLocation(dates, field.id, this.location()).subscribe({
      next: weather => {
        if (weather?.data && weather?.data.length) {
          // fill the table with the new weather data
          this.tableFormArray().setValue(this.convertWeatherDataRows(weather.data));
        } else {
          // no weather data found, create empty rows
          this.tableFormArray().setValue(dates.map(time => this.createEmptyRowWithTime(time)));
        }
      },
      complete: () => {
        this.markRowsAsEmpty(false);
        this.watchForTimeChangesForCurrentRow();
        setTimeout(() => {
          this.externalDataLoading.set(false);
        }, DEBOUNCE_TIMES.default);
      },
    });
  }

  private convertWeatherDataRows(rows: TableFieldValueType[]): TableFieldValueType[] {
    return [...(rows ?? [])].map((row, index) => {
      if (index === this.activeRowIndex()) {
        // convert the active row to user measurement system because the view components expect user measurement system values
        row = this.convertTableDataToUserMeasurementSystem(row);
      }
      return this.addNullValuesOnMissingFields(row);
    });
  }

  private loadWeatherDataForCurrentRow() {
    this.markCurrentRowAsEmpty(true);
    this.externalDataLoading.set(true);
    const associatedDate = this.getDateWithCustomTime();
    const field = this.fieldData();

    this.weatherService.getDataByTimeAndLocation(associatedDate, this.location(), field.id).subscribe({
      next: weather => {
        // set weather data
        this.setActiveFormData(weather.data, associatedDate);
      },
      complete: () => {
        //this will run on both success and error
        this.markCurrentRowAsEmpty(false);

        // listen for time changes so we can update the weather data when the time changes
        this.watchForTimeChangesForCurrentRow();

        // timeout is needed to prevent the time field from being marked as dirty
        // when the weather data is loaded, which would trigger the time field validation
        setTimeout(() => {
          // add back the time field validation
          this.addTimeFieldValidation();
          this.externalDataLoading.set(false);
        }, DEBOUNCE_TIMES.default);
      },
    });
  }

  private cancelWatchForTimeChangesForCurrentRow() {
    this.timeFieldSubscription?.unsubscribe();
  }

  private watchForTimeChangesForCurrentRow() {
    this.cancelWatchForTimeChangesForCurrentRow();
    const activeForm = this.getActiveForm();
    if (!activeForm) {
      return;
    }
    this.timeFieldSubscription = activeForm.controls[this.weatherTimeKey()].valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.default))
      .subscribe(newTimestamp => {
        // compare the last value with the new value
        // because of the data conversion the time value change is triggered even if the value is not changed
        if (this.activeRowIndex() === null || this.activeRowIndex() >= this.control().value.length) {
          return;
        }
        const oldTimestamp = this.control().value[this.activeRowIndex()][this.weatherTimeKey()] as string;
        if (oldTimestamp === newTimestamp) {
          return;
        }
        this.onWeatherTimeChangeForCurrentRow(newTimestamp as string);
      });
  }

  private onWeatherTimeChangeForCurrentRow(newTimestamp: string) {
    const associatedDate = this.getDateWithCustomTime(newTimestamp);
    const field = this.fieldData();
    this.markCurrentRowAsEmpty(true);
    this.externalDataLoading.set(true);
    this.weatherService
      .getDataByTimeAndLocation(associatedDate, this.location(), field.id, field.entityInstanceId)
      .subscribe({
        next: weather => this.setActiveFormData(weather.data, associatedDate),
        complete: () => {
          this.markCurrentRowAsEmpty(false);
          setTimeout(() => {
            this.externalDataLoading.set(false);
          }, DEBOUNCE_TIMES.default);
        },
      });
  }

  private markCurrentRowAsEmpty(empty: boolean) {
    const rows = [...(this.formValue() ?? [])];
    if (rows.length === 0) {
      // avoid changing control value if there are no rows
      return;
    }
    if (empty && (this.activeRowIndex() === null || this.activeRowIndex() >= rows.length)) {
      // if there is no active row, we need to add a new row to the table to show the skeleton loader
      rows.push({ empty: true });
    } else if (empty) {
      rows[this.activeRowIndex()]['empty'] = empty;
    } else {
      delete rows[this.activeRowIndex()]['empty'];
    }
    this.control().setValue(rows);
    this.cdr.markForCheck();
  }

  private markRowsAsEmpty(empty: boolean) {
    const rows = [...(this.formValue() ?? [])];
    if (rows.length === 0) {
      // avoid changing control value if there are no rows
      return;
    }
    rows.forEach(row => {
      if (empty) {
        row['empty'] = empty;
      } else {
        delete row['empty'];
      }
    });
    this.control().setValue(rows);
    this.cdr.markForCheck();
  }

  private getActiveForm() {
    return this.activeRowIndex() >= 0 && !!this.tableFormArray()
      ? this.tableFormArray().at(this.activeRowIndex())
      : null;
  }

  private setActiveFormData(data: TableFieldValueType, associatedDate: string) {
    const activeForm = this.getActiveForm();
    if (!activeForm) {
      return;
    }
    if (!data) {
      // no weather data available for the selected time, set the time field value to the selected time
      data = this.createEmptyRowWithTime(associatedDate);
    }
    activeForm.patchValue(this.convertTableDataToUserMeasurementSystem(data));
    this.markCurrentRowAsEmpty(false);
  }

  private createEmptyRowWithTime(time: string): TableFieldValueType {
    return this.addNullValuesOnMissingFields({
      [this.weatherTimeKey()]: time,
    } as TableFieldValueType);
  }

  private addNullValuesOnMissingFields(row: TableFieldValueType): TableFieldValueType {
    return {
      ...row,
      [this.weatherTemperatureKey()]: row[this.weatherTemperatureKey()] ?? null,
      [this.weatherWindKey()]: row[this.weatherWindKey()] ?? null,
      [this.weatherConditionsKey()]: row[this.weatherConditionsKey()] ?? null,
      [this.weatherPrecipitationKey()]: row[this.weatherPrecipitationKey()] ?? null,
    } as TableFieldValueType;
  }

  private convertTableDataToMetricMeasurementSystem(data: TableFieldValueType): TableFieldValueType {
    if (!data) {
      return;
    }
    return {
      ...data,
      [this.weatherTemperatureKey()]:
        this.utilsService.convertValueToMeasurementValue(
          data[this.weatherTemperatureKey()] as number,
          'temperature',
          this.userMeasurementSystem,
          'Metric',
        ) ?? null,
      [this.weatherWindKey()]:
        this.utilsService.convertValueToMeasurementValue(
          data[this.weatherWindKey()] as number,
          'speed',
          this.userMeasurementSystem,
          'Metric',
        ) ?? null,
      [this.weatherPrecipitationKey()]:
        this.utilsService.convertValueToMeasurementValue(
          data[this.weatherPrecipitationKey()] as number,
          'precipitation',
          this.userMeasurementSystem,
          'Metric',
        ) ?? null,
    };
  }

  private convertTableDataToUserMeasurementSystem(data: TableFieldValueType): TableFieldValueType {
    if (!data) {
      return;
    }
    return {
      ...data,
      [this.weatherTemperatureKey()]:
        this.utilsService.convertValueToMeasurementValue(
          data[this.weatherTemperatureKey()] as number,
          'temperature',
          'Metric',
          this.userMeasurementSystem,
        ) ?? null,
      [this.weatherWindKey()]:
        this.utilsService.convertValueToMeasurementValue(
          data[this.weatherWindKey()] as number,
          'speed',
          'Metric',
          this.userMeasurementSystem,
        ) ?? null,
      [this.weatherPrecipitationKey()]:
        this.utilsService.convertValueToMeasurementValue(
          data[this.weatherPrecipitationKey()] as number,
          'precipitation',
          'Metric',
          this.userMeasurementSystem,
        ) ?? null,
    };
  }

  /** Returns a date string with the time set to the provided timestamp or the current time if none is provided
   * @param newTimestamp The timestamp to set the time to. If not provided, the current time will be used.
   */
  private getDateWithCustomTime(newTimestamp?: string) {
    const ianaTimeZoneId = this.userStore.userData().ianaTimeZoneId;
    const dateStr = this.date();

    // Date-only fields are stored as midnight UTC ("2026-02-16T00:00:00.000Z").
    // Converting midnight UTC to a negative-offset timezone (e.g. PST = UTC-8) rolls the
    // calendar date back by one day, so we must take the date part as-is without conversion.
    // DateTime fields have a real time component, so we convert to the user's timezone first
    // to get the calendar date as the user sees it.
    const isDateOnly = dateStr.includes('T00:00:00'); // to cover cases when the date contains time part

    const datePart = isDateOnly
      ? dateStr.split('T')[0] // "2026-02-16" — no TZ conversion
      : moment.tz(dateStr, ianaTimeZoneId).format('YYYY-MM-DD'); // e.g. "2026-02-16T12:24:06Z" -> "2026-02-16" in PST

    // create a new date time with the date part and the current time in the user's timezone, then convert to ISO string
    const selectedDate = moment.tz(datePart, 'YYYY-MM-DD', ianaTimeZoneId);
    // if there is a new timestamp, use its time part, otherwise use the current time in the user's timezone
    const timePart = newTimestamp ? moment.tz(newTimestamp, ianaTimeZoneId) : moment.tz(ianaTimeZoneId);

    selectedDate
      .hours(timePart.hours())
      .minutes(timePart.minutes())
      .seconds(timePart.seconds())
      .milliseconds(timePart.milliseconds());

    return selectedDate.toISOString();
  }

  private requiredTimeField(timeFieldKey: string): ValidatorFn {
    return (control: AbstractControl): ValidatorResponse | null => {
      var rows = control.value as TableFieldValueType[];
      if (rows?.length) {
        const invalid = rows.some(
          row => row[timeFieldKey] === null || row[timeFieldKey] === undefined || row[timeFieldKey] === '',
        );
        if (invalid) {
          return { requiredTimeField: true };
        }
      }
      return null;
    };
  }
}
